import React from 'react';

/**
 * Generic app-level error boundary.
 *
 * Wrap any subtree (e.g. the Screener results or Watchlist table) so that an
 * uncaught render-phase exception renders a styled fallback card with a Reload
 * button instead of unmounting the React tree — which would otherwise leave the
 * user staring at the dark #121212 body background ("the screen goes black").
 *
 * Pass an optional `label` to customize the heading for the wrapped surface.
 * Pass an optional `onError(error, errorInfo)` callback to forward the caught
 * exception to logging/monitoring (seam for a future Sentry-style hook).
 */

// Cached result payloads that, if stale/malformed, can crash the table on
// render. The "Clear cached results & reload" recovery wipes these so the next
// load fetches fresh data instead of re-hydrating the bad payload.
const CACHE_KEYS = [
  'screener_last_results',
  'screener_last_results_timestamp',
  'watchlist_last_data',
  'watchlist_collapsed_groups',
  'expandedCard_indicators',
  'expandedCard_indicatorSettings',
  'expandedCard_showBollinger',
  'zerodte_last_results',
  'zerodte_last_results_timestamp',
  'zeroDteChart_indicators',
  'zeroDteChart_indicatorSettings',
  'zeroDteChart_showBollinger',
];

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught an error:', error);
    console.error('Component Stack:', errorInfo?.componentStack);
    this.setState({ error, errorInfo });
    // Forward to an optional monitoring hook so prod occurrences are captured.
    if (typeof this.props.onError === 'function') {
      try {
        this.props.onError(error, errorInfo);
      } catch (hookErr) {
        console.error('ErrorBoundary onError hook threw:', hookErr);
      }
    }
  }

  clearCacheAndReload = () => {
    try {
      CACHE_KEYS.forEach((key) => localStorage.removeItem(key));
    } catch (e) {
      console.error('Failed clearing cached results:', e);
    }
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const label = this.props.label || 'This section';
      return (
        <div className="bg-red-900/20 border border-red-500 rounded-lg p-6 m-4">
          <h2 className="text-xl font-bold text-red-400 mb-4">
            {label} ran into a problem
          </h2>

          <div className="space-y-4">
            <div className="bg-background/50 p-4 rounded">
              <p className="text-text/80 text-sm">
                Something went wrong while rendering. Your data is safe — try
                reloading the page. If this keeps happening, clearing the cached
                results and re-running usually resolves it.
              </p>
            </div>

            <details className="bg-background/50 p-4 rounded">
              <summary className="font-semibold text-yellow-400 cursor-pointer">
                Technical Details (click to expand)
              </summary>
              <div className="mt-4 space-y-2">
                <div>
                  <strong className="text-text/60">Error:</strong>
                  <pre className="text-xs text-text/40 mt-1 overflow-x-auto whitespace-pre-wrap">
                    {this.state.error?.toString()}
                  </pre>
                </div>
                <div>
                  <strong className="text-text/60">Stack Trace:</strong>
                  <pre className="text-xs text-text/40 mt-1 overflow-x-auto">
                    {this.state.error?.stack}
                  </pre>
                </div>
                <div>
                  <strong className="text-text/60">Component Stack:</strong>
                  <pre className="text-xs text-text/40 mt-1 overflow-x-auto">
                    {this.state.errorInfo?.componentStack}
                  </pre>
                </div>
              </div>
            </details>

            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => window.location.reload()}
                className="bg-primary text-dark px-4 py-2 rounded hover:bg-accent transition-colors"
              >
                Reload Page
              </button>
              <button
                onClick={this.clearCacheAndReload}
                className="bg-background/60 border border-yellow-500/60 text-yellow-300 px-4 py-2 rounded hover:bg-yellow-500/10 transition-colors"
                title="Removes locally cached screener/watchlist results, then reloads with fresh data"
              >
                Clear cached results &amp; reload
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
